import {
	SupportedAgent,
	SupportedFeature,
	type SupportedModel,
} from "@src/gql/graphql";

import IconClaude from "@src/assets/icons/models/icon-claude.svg?react";
import IconDeepseek from "@src/assets/icons/models/icon-deepseek.svg?react";
import IconGemini from "@src/assets/icons/models/icon-gemini.svg?react";
import IconGemma from "@src/assets/icons/models/icon-gemma.svg?react";
import IconGpt from "@src/assets/icons/models/icon-gpt.svg?react";
import IconGrok from "@src/assets/icons/models/icon-grok.svg?react";
import IconKimi from "@src/assets/icons/models/icon-kimi.svg?react";
import IconMoonshot from "@src/assets/icons/models/icon-moonshot.svg?react";
import IconNanoBanana from "@src/assets/icons/models/icon-nano-banana.svg?react";
import IconNova from "@src/assets/icons/models/icon-nova.svg?react";
import IconStableDiffusion from "@src/assets/icons/models/icon-stable-diffusion.svg?react";
import IconZaiGlm from "@src/assets/icons/zai-glm-model.svg?react";

import IconClaudeColored from "@src/assets/icons/models/icon-claude-colored.svg?react";
import IconDeepseekColored from "@src/assets/icons/models/icon-deepseek-colored.svg?react";
import IconGeminiColored from "@src/assets/icons/models/icon-gemini-colored.svg?react";
import IconGemmaColored from "@src/assets/icons/models/icon-gemma-colored.svg?react";
import IconGptColored from "@src/assets/icons/models/icon-gpt-colored.svg?react";
import IconKimiColored from "@src/assets/icons/models/icon-kimi-colored.svg?react";
import IconNanoBananaColored from "@src/assets/icons/models/icon-nano-banana-colored.svg?react";
import IconNovaColored from "@src/assets/icons/models/icon-nova-colored.svg?react";
import IconStableDiffusionColored from "@src/assets/icons/models/icon-stable-diffusion-colored.svg?react";

import IconChartNetwork from "@src/assets/icons/lucide/chart-network.svg?react";
import IconImage from "@src/assets/icons/lucide/image.svg?react";
import IconSparkles from "@src/assets/icons/lucide/sparkles.svg?react";
import { UNKNOWN_MODEL_ICON } from "@src/common/constants";
import {
	type ModelProvider,
	getModelDisplayName,
	resolveModelIconProvider,
} from "@src/components/cards/utils/model-presentation";

type ModelIcon = React.FC<React.SVGProps<SVGSVGElement>>;

const PROMPT_MODEL_ICONS: Partial<Record<ModelProvider, ModelIcon>> = {
	gpt: IconGpt,
	claude: IconClaude,
	gemini: IconGemini,
	gemma: IconGemma,
	moonshot: IconMoonshot,
	nova: IconNova,
	deepSeek: IconDeepseek,
	grok: IconGrok,
	kimi: IconKimi,
	glm: IconZaiGlm,
	stableDiffusion: IconStableDiffusion,
	nanoBanana: IconNanoBanana,
};

const PROMPT_MODEL_COLORED_ICONS: Partial<Record<ModelProvider, ModelIcon>> = {
	gpt: IconGptColored,
	claude: IconClaudeColored,
	gemini: IconGeminiColored,
	gemma: IconGemmaColored,
	moonshot: IconMoonshot,
	nova: IconNovaColored,
	deepSeek: IconDeepseekColored,
	grok: IconGrok,
	kimi: IconKimiColored,
	glm: IconZaiGlm,
	stableDiffusion: IconStableDiffusionColored,
	nanoBanana: IconNanoBananaColored,
};

export const getPromptModelData = (model: SupportedModel) => {
	const provider = resolveModelIconProvider(model);
	return {
		name: getModelDisplayName(model),
		icon: PROMPT_MODEL_ICONS[provider] ?? UNKNOWN_MODEL_ICON,
		coloredIcon: PROMPT_MODEL_COLORED_ICONS[provider] ?? UNKNOWN_MODEL_ICON,
	};
};

const getPromptAgentName = (agent: SupportedAgent) => {
	const agentCaptions = {
		[SupportedAgent.AiAssistant]: "Daily Assistant",
		[SupportedAgent.ImageMaker]: "Image Maker",
		[SupportedAgent.ThinkingPartner]: "Strategic Thinker",
	};

	return agentCaptions[agent];
};

const getPromptAgentDescription = (agent: SupportedAgent) => {
	const agentCaptions = {
		[SupportedAgent.AiAssistant]: "Your go-to assistant for daily tasks",
		[SupportedAgent.ImageMaker]: "Turns your descriptions into visuals",
		[SupportedAgent.ThinkingPartner]: "Your thinking partner for complex tasks",
	};

	return agentCaptions[agent];
};

const getPromptAgentIcon = (agent: SupportedAgent) => {
	const agentCaptions = {
		[SupportedAgent.AiAssistant]: IconSparkles,
		[SupportedAgent.ImageMaker]: IconImage,
		[SupportedAgent.ThinkingPartner]: IconChartNetwork,
	};

	return agentCaptions[agent];
};

export const getPromptAgentSupportedFeature = (agent: SupportedAgent) => {
	const agentCaptions = {
		[SupportedAgent.AiAssistant]: null,
		[SupportedAgent.ImageMaker]: SupportedFeature.ImageMaker,
		[SupportedAgent.ThinkingPartner]: SupportedFeature.ThinkingPartner,
	};

	return agentCaptions[agent];
};

export const getPromptAgentData = (agent: SupportedAgent) => ({
	name: getPromptAgentName(agent),
	description: getPromptAgentDescription(agent),
	icon: getPromptAgentIcon(agent),
	supportedFeature: getPromptAgentSupportedFeature(agent),
});

export { isSupportedModel } from "@src/components/cards/utils/model-id";
